import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ShowCard from "@/components/base/ShowCard";
import Pagination from "@/components/base/Pagination";
import { shows } from "@/mocks/tv/shows";
import { sortOptions, typeMetas } from "@/mocks/tv/filters";
import { filterShows, getShowsByType, sortShows, type FilterState } from "@/utils/tv";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import FilterPanel, { type FilterGroup } from "./components/FilterPanel";

const PAGE_SIZE = 12;

export default function Category() {
  const { type = "movie" } = useParams();
  const meta = typeMetas.find((item) => item.key === type);
  const baseList = useMemo(() => getShowsByType(type), [type]);

  const [filter, setFilter] = useState<FilterState>({ genres: [], regions: [], years: [] });
  const [sort, setSort] = useState("latest");
  const [page, setPage] = useState(1);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  const { title, description, keywords } = meta
    ? {
        title: `绍宋 - ${meta.label}${meta.titleSuffix}大全`,
        description: meta.description,
        keywords: `绍宋,${meta.label},${meta.label}资源,${meta.label}大全,在线视频`,
      }
    : {
        title: "绍宋 - 分类影视列表",
        description: "绍宋影视分类页面，按类型、地区、年份筛选最新影视作品。",
        keywords: "绍宋,影视分类,影视筛选",
      };

  useDocumentMeta({ title, description, keywords });

  const filtered = useMemo(() => sortShows(filterShows(baseList, filter), sort), [baseList, filter, sort]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paged = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const hasActiveFilter = filter.genres.length + filter.regions.length + filter.years.length > 0;

  const onToggle = (group: FilterGroup, value: string) => {
    setFilter((prev) => {
      const current = prev[group];
      const next = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      return { ...prev, [group]: next };
    });
    setPage(1);
  };

  const onReset = () => {
    setFilter({ genres: [], regions: [], years: [] });
    setPage(1);
  };

  if (!meta) {
    return (
      <div className="min-h-screen flex flex-col bg-background-50">
        <Navbar />
        <main className="flex-1 flex items-center justify-center px-4 pt-16">
          <div className="text-center max-w-md">
            <span className="inline-flex w-16 h-16 rounded-full bg-background-100 items-center justify-center mb-4">
              <i className="ri-error-warning-line text-3xl text-foreground-400" />
            </span>
            <h1 className="font-heading text-2xl font-bold text-foreground-900">分类不存在</h1>
            <p className="mt-2 text-sm text-foreground-500">抱歉，你访问的分类页面不存在或已下线。</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 mt-6 h-11 px-6 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-home-4-line text-lg w-5 h-5 flex items-center justify-center" />
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <Navbar />

      <main className="flex-1 pt-16">
        <div className="w-full px-4 md:px-8 py-8 md:py-10">
          <nav className="flex items-center gap-1.5 text-xs text-foreground-400 mb-4" aria-label="面包屑">
            <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line text-base w-4 h-4 flex items-center justify-center" />
            <span>分类</span>
            <i className="ri-arrow-right-s-line text-base w-4 h-4 flex items-center justify-center" />
            <span className="text-foreground-700">{meta.label}</span>
          </nav>

          <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900">
            {meta.label}影视列表
          </h1>
          <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-3xl leading-7">
            {meta.description}
          </p>
        </div>

        <div className="w-full px-4 md:px-8 pb-14">
          <div className="flex flex-col lg:flex-row gap-6">
            <aside className="hidden lg:block w-64 shrink-0">
              <div className="sticky top-20">
                <FilterPanel
                  selectedGenres={filter.genres}
                  selectedRegions={filter.regions}
                  selectedYears={filter.years}
                  onToggle={onToggle}
                  onReset={onReset}
                  resultCount={filtered.length}
                  hasActiveFilter={hasActiveFilter}
                />
              </div>
            </aside>

            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-3 mb-5">
                <button
                  type="button"
                  onClick={() => setMobileFilterOpen((v) => !v)}
                  className="lg:hidden inline-flex items-center gap-2 h-10 px-4 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-700 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-filter-3-line text-base w-4 h-4 flex items-center justify-center" />
                  筛选
                  {hasActiveFilter && (
                    <span className="w-5 h-5 rounded-full bg-accent-500 text-primary-950 text-[11px] flex items-center justify-center">
                      {filter.genres.length + filter.regions.length + filter.years.length}
                    </span>
                  )}
                </button>

                <div className="flex items-center gap-1.5 ml-auto bg-background-100 rounded-full p-1">
                  {sortOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => {
                        setSort(option.value);
                        setPage(1);
                      }}
                      className={`px-3.5 py-1.5 text-xs rounded-full transition-colors cursor-pointer whitespace-nowrap ${
                        sort === option.value
                          ? "bg-background-50 text-primary-600 font-medium"
                          : "text-foreground-500 hover:text-foreground-800"
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {mobileFilterOpen && (
                <div className="lg:hidden mb-5">
                  <FilterPanel
                    selectedGenres={filter.genres}
                    selectedRegions={filter.regions}
                    selectedYears={filter.years}
                    onToggle={onToggle}
                    onReset={onReset}
                    resultCount={filtered.length}
                    hasActiveFilter={hasActiveFilter}
                  />
                </div>
              )}

              {paged.length > 0 ? (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                    {paged.map((show) => (
                      <ShowCard key={show.id} show={show} />
                    ))}
                  </div>
                  <Pagination current={safePage} total={totalPages} onChange={setPage} />
                </>
              ) : (
                <div className="flex flex-col items-center justify-center py-20 text-center border border-dashed border-background-300 rounded-lg">
                  <span className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mb-4">
                    <i className="ri-search-eye-line text-3xl text-foreground-400" />
                  </span>
                  <h3 className="font-heading text-lg font-bold text-foreground-900">没有找到符合条件的作品</h3>
                  <p className="mt-2 text-sm text-foreground-500">试试调整或重置筛选条件，发现更多精彩内容。</p>
                  <button
                    type="button"
                    onClick={onReset}
                    className="mt-5 inline-flex items-center gap-2 h-10 px-5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-refresh-line text-base w-4 h-4 flex items-center justify-center" />
                    重置筛选
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}